<script setup lang="ts">
import OzBaseButton from '@@/library/v4/components/OzBaseButton.vue'
import OzIcon from '@@/library/v4/components/OzIcon.vue'
import { computed } from 'vue'

const {
  darkMode = 'auto',
  value = false,
  disabled = false,
  sizePreset = OzToggleSizePreset.H24px,
} = defineProps<{
  darkMode?: boolean | 'auto'
  value?: boolean
  disabled?: boolean
  sizePreset?: OzToggleSizePreset
}>()

const emit = defineEmits<{
  (name: 'input', value: boolean): void
}>()

const toggleDimensions = computed(() => ({
  'h-5 w-8': sizePreset === OzToggleSizePreset.H20px, // 20px height, 32px width
  'h-6 w-10': sizePreset === OzToggleSizePreset.H24px, // 24px height, 40px width
  'h-7 w-12': sizePreset === OzToggleSizePreset.H28px, // 28px height, 48px width
}))

const indicatorDimensions = computed(() => ({
  'h-4 w-4': sizePreset === OzToggleSizePreset.H20px, // 16px indicator
  'h-5 w-5': sizePreset === OzToggleSizePreset.H24px, // 20px indicator
  'h-6 w-6': sizePreset === OzToggleSizePreset.H28px, // 24px indicator
}))

const indicatorPosition = computed(() => {
  if (sizePreset === OzToggleSizePreset.H20px) {
    return value ? 'start-3.5' : 'start-0.5'
  } else if (sizePreset === OzToggleSizePreset.H24px) {
    return value ? 'start-4.5' : 'start-0.5'
  } else if (sizePreset === OzToggleSizePreset.H28px) {
    return value ? 'start-5.5' : 'start-0.5'
  }
  return 'start-0.5' // fallback
})

const indicatorIconSize = computed(() => {
  if (sizePreset === OzToggleSizePreset.H20px) {
    return 10
  } else if (sizePreset === OzToggleSizePreset.H24px) {
    return 12
  } else if (sizePreset === OzToggleSizePreset.H28px) {
    return 14
  }
  return 10
})

const onChange = (): void => {
  if (disabled) return
  emit('input', value)
}
</script>

<script lang="ts">
export enum OzToggleSizePreset {
  H20px = 'H20px',
  H24px = 'H24px',
  H28px = 'H28px',
}

export default {}
</script>

<template>
  <OzBaseButton
    :class="[
      'box-border',
      'relative',
      'rounded-full',
      'outline-hidden',
      'transition-all',
      'motion-reduce:transition-none',
      'duration-250',
      'p-0.5',
      // Size-based dimensions and padding
      toggleDimensions,
      // Cursor
      disabled ? 'cursor-not-allowed' : 'cursor-pointer',
      // Focus ring
      'ring-offset-2',
      'focus-visible:ring-2',
      // Focus ring color
      {
        'ring-grape-500': darkMode === false,
        'ring-canary-500': darkMode === true,
        'ring-grape-500 dark:ring-canary-500': darkMode === 'auto',
      },
      // Focus ring offset color
      {
        'ring-offset-light-ui-100': darkMode === false,
        'ring-offset-dark-ui-100': darkMode === true,
        'ring-offset-light-ui-100 dark:ring-offset-dark-ui-100': darkMode === 'auto',
      },
      // Background colors
      disabled &&
        value && {
          'bg-grape-0': darkMode === false,
          'bg-canary-1000': darkMode === true,
          'bg-grape-0 dark:bg-canary-1000': darkMode === 'auto',
        },
      disabled &&
        !value && {
          'bg-grey-100': darkMode === false,
          'bg-grey-700': darkMode === true,
          'bg-grey-100 dark:bg-grey-700': darkMode === 'auto',
        },
      !disabled &&
        value && {
          'bg-grape-500 hhover:bg-grape-400 active:bg-grape-600': darkMode === false,
          'bg-canary-500 hhover:bg-canary-400 active:bg-canary-600': darkMode === true,
          'bg-grape-500 dark:bg-canary-500 hhover:bg-grape-400 hhover:dark:bg-canary-400 active:bg-grape-600 active:dark:bg-canary-600':
            darkMode === 'auto',
        },
      !disabled &&
        !value && {
          'bg-grey-200 hhover:bg-grey-100 active:bg-grey-300': darkMode === false,
          'bg-grey-600 hhover:bg-grey-500 active:bg-grey-500': darkMode === true,
          'bg-grey-200 hhover:bg-grey-100 dark:bg-grey-600 hhover:dark:bg-grey-500 active:bg-grey-300 active:dark:bg-grey-500':
            darkMode === 'auto',
        },
    ]"
    :disabled="disabled"
    type="button"
    role="switch"
    :aria-checked="String(value)"
    tabindex="0"
    @click="onChange"
  >
    <!-- Toggle indicator (circle) -->
    <div
      :class="[
        'absolute',
        'top-0.5',
        'rounded-full',
        'transition-all',
        'motion-reduce:transition-none',
        'duration-250',
        'flex',
        'items-center',
        'justify-center',
        'shadow-lg',
        // Size-based dimensions and positioning
        indicatorDimensions,
        indicatorPosition,
        // Cursor
        disabled ? 'cursor-not-allowed' : 'cursor-pointer',
        // Indicator background colors
        disabled && {
          'bg-white/90': darkMode === false,
          'bg-dark-text-300': darkMode === true,
          'bg-white/90 dark:bg-dark-text-300': darkMode === 'auto',
        },
        !disabled && {
          'bg-white': darkMode === false,
          'bg-dark-ui-100': darkMode === true,
          'bg-white dark:bg-dark-ui-100': darkMode === 'auto',
        },
      ]"
    >
      <OzIcon
        :name="value ? 'checkmark' : 'close_small'"
        :size="indicatorIconSize"
        :class="[
          disabled &&
            value && {
              'text-grape-100': darkMode === false,
              'text-grey-600': darkMode === true,
              'text-grape-100 dark:text-grey-600': darkMode === 'auto',
            },
          disabled &&
            !value && {
              'text-grey-200': darkMode === false,
              'text-grey-600': darkMode === true,
              'text-grey-200 dark:text-grey-600': darkMode === 'auto',
            },
          !disabled &&
            value && {
              'text-grape-500': darkMode === false,
              'text-canary-300': darkMode === true,
              'text-grape-500 dark:text-canary-300': darkMode === 'auto',
            },
          !disabled && !value && 'text-grey-500 hhover:text-grey-300',
        ]"
      />
    </div>
  </OzBaseButton>
</template>
